import { useEffect, useMemo, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/pagination";
import { Autoplay, Navigation, Pagination } from "swiper/modules";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

const TEST_REVIEWS = [
  {
    id: "test-1",
    name: "Ayla K.",
    text: "Gercekten el emegi oldugu belli. Urunler cok kaliteli ve guzel paketlenmisti. Bayildim!",
    rating: 5,
  },
  {
    id: "test-2",
    name: "Zeynep T.",
    text: "The wall art I received added a lot of soul to my space. Highly recommended!",
    rating: 4,
  },
  {
    id: "test-3",
    name: "Meryem S.",
    text: "Harika musteri hizmeti, urun hizli ve ozenli geldi. Tesekkurler!",
    rating: 5,
  },
  {
    id: "test-4",
    name: "Ali D.",
    text: "Beautiful craftsmanship. Fast delivery, will definitely order again.",
    rating: 5,
  },
  {
    id: "test-5",
    name: "Sarah M.",
    text: "Loved the natural therapy items. They smell amazing and are well-made.",
    rating: 4,
  },
  {
    id: "test-6",
    name: "Hande K.",
    text: "Tasarim cok ozgun ve kaliteli. Kargolama da cok iyiydi.",
    rating: 5,
  },
  {
    id: "test-7",
    name: "Yusuf E.",
    text: "Bekledigimden cok daha iyi cikti. Elinize saglik!",
    rating: 5,
  },
  {
    id: "test-8",
    name: "Derya L.",
    text: "Nice packaging and quality materials. Great experience.",
    rating: 4,
  },
  {
    id: "test-9",
    name: "Fatma G.",
    text: "Harika bir hediye oldu. Cok tesekkur ederim.",
    rating: 5,
  },
  {
    id: "test-10",
    name: "Mehmet T.",
    text: "Musteri hizmetleri cok ilgiliydi, memnun kaldim.",
    rating: 5,
  },
];

function ReviewsSlider() {
  const [productReviews, setProductReviews] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const loadRecentReviews = async () => {
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/reviews/recent?limit=12`,
        );
        if (!response.ok) return;

        const data = await response.json();
        const normalized = (data?.reviews || [])
          .map((review) => ({
            id: `db-${review.review_id}`,
            source: "product",
            name:
              `${review?.User?.first_name || ""} ${review?.User?.last_name || ""}`.trim() ||
              "Customer",
            text:
              review?.comment?.trim() ||
              `Great experience with ${review?.Product?.name_en || "this product"}.`,
            rating: Math.min(Math.max(Number(review?.rating) || 5, 1), 5),
            productName: review?.Product?.name_en || "",
          }))
          .filter((review) => review.text.length > 0);

        if (isMounted) {
          setProductReviews(normalized);
        }
      } catch (error) {
        // Keep slider functional even if review API is unavailable.
        if (isMounted) setProductReviews([]);
      }
    };

    loadRecentReviews();

    return () => {
      isMounted = false;
    };
  }, []);

  const allReviews = useMemo(
    () => [...TEST_REVIEWS, ...productReviews],
    [productReviews],
  );

  return (
    <div
      className="relative w-[94%] sm:w-[90%] px-2 sm:px-12 mb-8 mx-auto"
      dir="ltr"
    >
      <button
        type="button"
        className="reviews-prev hidden sm:block absolute left-0 top-1/2 z-10 -translate-y-1/2 rounded-full border border-[#e7d9df] bg-white/95 p-2 text-[#7f5164] shadow-md transition-colors hover:bg-[#f6eef2]"
        aria-label="Previous review"
      >
        <span aria-hidden="true">&#10094;</span>
      </button>
      <button
        type="button"
        className="reviews-next hidden sm:block absolute right-0 top-1/2 z-10 -translate-y-1/2 rounded-full border border-[#e7d9df] bg-white/95 p-2 text-[#7f5164] shadow-md transition-colors hover:bg-[#f6eef2]"
        aria-label="Next review"
      >
        <span aria-hidden="true">&#10095;</span>
      </button>

      <Swiper
        dir="ltr"
        autoplay={{ delay: 2600, disableOnInteraction: false }}
        navigation={{ prevEl: ".reviews-prev", nextEl: ".reviews-next" }}
        pagination={{ clickable: true }}
        spaceBetween={20}
        modules={[Autoplay, Pagination, Navigation]}
        breakpoints={{
          0: {
            slidesPerView: 1,
            spaceBetween: 14,
          },
          640: {
            slidesPerView: 2,
            spaceBetween: 18,
          },
          768: {
            slidesPerView: 3,
            spaceBetween: 20,
          },
          1200: {
            slidesPerView: 4,
            spaceBetween: 20,
          },
        }}
        className="reviews-swiper w-[full] pb-[50px]"
        style={{ paddingBottom: "50px" }}
      >
        {allReviews.map((review) => (
          <SwiperSlide key={review.id} className="!h-auto">
            <div className="h-full lg:min-h-[185px] min-h-[185px] rounded-xl border border-[#f0e3e8] bg-white shadow-md p-5 sm:p-6 text-gray-800">
              <div className="mb-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4">
                <h3 className="font-bold text-[#7e3957] w-full sm:w-auto">
                  {review.name}
                </h3>
                <div className="shrink-0 text-yellow-400 text-sm self-start sm:self-auto">
                  {"★".repeat(review.rating)}
                  {"☆".repeat(5 - review.rating)}
                </div>
              </div>

              {review.source === "product" && (
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-[#f6e8ef] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#7e3957]">
                    Verified Product Review
                  </span>
                  {review.productName && (
                    <span className="text-xs font-medium text-[#915f73] truncate">
                      {review.productName}
                    </span>
                  )}
                </div>
              )}

              <p className="text-[0.95rem] sm:text-sm leading-6 text-gray-700 md:line-clamp-4">
                {review.text}
              </p>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}

export default ReviewsSlider;
